import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';

import {
  EpidemiologyError,
  EpidemiologyRow,
  IPatientHistory,
  PatientRequestsService,
} from '@app/core/patient-requests';
import { UsersService } from '@app/core/users';
import { MsgboxService } from '@app/shared/msgbox';
import {
  formatPatientRequestDateOnly,
  formatPatientRequestDateTime,
  patientRequestDateOnlyForFilter,
} from '../patient-request-date-format.util';

/** Fila tabla con campos auxiliares para ordenación/filtros PrimeNG (P.3.2). */
export type EpidemiologyTableRow = EpidemiologyRow & {
  formattedEntryDate: string;
  isolateDisplay: string;
  /** Fecha a medianoche local para `p-columnFilter type="date"`. */
  entryDateForFilter: Date;
  /** Valor booleano normalizado para ordenar/filtrar la columna ATB. */
  atbForFilter: boolean;
};

type EpidemiologyDateInput = string | Date | null | undefined;

/** Pantalla 3.2 — tabla epidemiología (SIS005 / DSI-003 §2.3.1 P.3.2). */
@Component({
  selector: 'afoone-epidemiology-table-view',
  templateUrl: './epidemiology-table-view.component.html',
  styleUrls: ['./epidemiology-table-view.component.css'],
})
export class EpidemiologyTableViewComponent implements OnInit, OnDestroy {
  patient: IPatientHistory | null = null;
  rows: EpidemiologyRow[] = [];
  tableRowsModel: EpidemiologyTableRow[] = [];
  loading = true;
  error: string | null = null;

  tableRows = 10;
  readonly tableRowsOptions = [5, 10, 15, 20, 50];

  private patientId = '';
  private labNr = 0;
  private dateFrom = '';
  private dateTo = '';
  private centroId: number | null = null;
  /** Código laboratorio 3 letras para `view-request`. */
  detailLaboratorio = 'MIC';

  private destroyed$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: PatientRequestsService,
    private keycloak: KeycloakService,
    private userService: UsersService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private msgbox: MsgboxService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.pipe(takeUntil(this.destroyed$)).subscribe(async (qp) => {
      this.patientId = String(qp['patientId'] ?? '').trim();
      this.labNr = Number(qp['labNr'] ?? 0);
      this.dateFrom = String(qp['dateFrom'] ?? '').trim();
      this.dateTo = String(qp['dateTo'] ?? '').trim();
      const c = qp['centroId'];
      if (c != null && c !== '') {
        const n = Number(c);
        if (!Number.isNaN(n)) {
          this.centroId = n;
          this.userService.setSisifoDefaultCenter(n);
        }
      }
      const lab = String(qp['detailLaboratorio'] ?? '').trim();
      if (lab.length >= 1) {
        this.detailLaboratorio = lab.slice(0, 3).toUpperCase();
      }

      if (!this.patientId || !this.labNr || !this.dateFrom || !this.dateTo) {
        this.loading = false;
        this.error = this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.MISSING_PARAMS');
        return;
      }

      await this.loadAll();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get dateRangeLabel(): string {
    if (!this.dateFrom || !this.dateTo) return '';
    return `${this.formatDateOnlyDisplay(this.dateFrom)} - ${this.formatDateOnlyDisplay(this.dateTo)}`;
  }

  get atbFilterOptions(): Array<{ label: string; value: boolean }> {
    return [
      {
        label: this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.ATB_YES'),
        value: true,
      },
      {
        label: this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.ATB_NO'),
        value: false,
      },
    ];
  }

  get canOpenSensitivityAtb(): boolean {
    return this.tableRowsModel.some((row) => row.atbForFilter);
  }

  private async waitForToken(maxWaitMs = 5000): Promise<boolean> {
    const deadline = Date.now() + maxWaitMs;
    while (Date.now() < deadline) {
      const token = await this.keycloak.getToken();
      if (token) return true;
      await new Promise((r) => setTimeout(r, 150));
    }
    return false;
  }

  private async loadAll(): Promise<void> {
    this.loading = true;
    this.error = null;
    this.rows = [];
    this.tableRowsModel = [];

    const hasToken = await this.waitForToken();
    if (!hasToken) {
      this.loading = false;
      this.error = this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.NO_SESSION_TAB');
      return;
    }

    const centerId = this.currentCenterId();

    this.service
      .searchEpidemiology({
        patientId: this.patientId,
        labNr: this.labNr,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        centroId: centerId,
      })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.rows = Array.isArray(res.data) ? res.data : [];
          this.tableRowsModel = this.rows.map((r) =>
            this.toTableRow(r)
          );
          this.loadPatientAuxiliary(centerId);
        },
        error: (err: unknown) => {
          this.loading = false;
          if (err instanceof EpidemiologyError) {
            if (err.msgCode === 'EPIDEMIOLOGY_NOT_FOUND') {
              const msg = this.translate.instant(`WEB_RESULTS.EPIDEMIOLOGY.${err.msgCode}`);
              this.toastr.info(msg);
              return;
            }
            if (err.msgCode === 'DB_ERROR_SEARCHING') {
              this.msgbox.showError(`WEB_RESULTS.EPIDEMIOLOGY.${err.msgCode}`);
              return;
            }
            const key = err.msgCode ? `WEB_RESULTS.EPIDEMIOLOGY.${err.msgCode}` : 'WEB_RESULTS.EPIDEMIOLOGY.DB_ERROR';
            if (['DB_ERROR_SEARCHING', 'DB_ERROR'].includes(err.msgCode)) {
              this.msgbox.showError(key);
            } else {
              this.toastr.warning(this.translate.instant(key));
            }
            return;
          }
          const message = typeof (err as { message?: string })?.message === 'string' ? (err as Error).message : '';
          this.error =
            message || this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.DB_ERROR');
        },
      });
  }

  private currentCenterId(): number | null {
    return this.centroId ?? this.userService.getSisifoDefaultCenter();
  }

  private loadPatientAuxiliary(centerId: number | null): void {
    this.service
      .getPatientHistories({
        history: this.patientId,
        searchType: 'C',
        centroId: centerId,
      } as any)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (list) => {
          const historiaNorm = this.patientId.trim();
          this.patient =
            Array.isArray(list) ? list.find((p) => (p.historia?.trim() ?? '') === historiaNorm) ?? null : null;
        },
        error: () => {},
      });
  }

  formatEntryDateDisplay(v: EpidemiologyDateInput): string {
    return formatPatientRequestDateTime(v);
  }

  private formatDateOnlyDisplay(v: EpidemiologyDateInput): string {
    return formatPatientRequestDateOnly(v);
  }

  isolateDisplay(row: EpidemiologyRow): string {
    const s = String(row.isolate ?? '').trim();
    if (s === '') {
      return this.translate.instant('WEB_RESULTS.EPIDEMIOLOGY.NO_ISOLATE');
    }
    return s;
  }

  private toTableRow(r: EpidemiologyRow): EpidemiologyTableRow {
    return {
      ...r,
      formattedEntryDate: this.formatEntryDateDisplay(r.entryDate),
      isolateDisplay: this.isolateDisplay(r),
      entryDateForFilter: this.entryDateAsFilterDate(r.entryDate),
      atbForFilter: this.atbIsPositive(r),
    };
  }

  /** Fecha calendario (sin hora) para filtro/ordenación por columna tipo fecha. */
  private entryDateAsFilterDate(v: EpidemiologyDateInput): Date {
    return patientRequestDateOnlyForFilter(v);
  }

  atbIsPositive(row: EpidemiologyRow): boolean {
    const raw = row as EpidemiologyRow & { aTB?: unknown; ATB?: unknown };
    const v = row.atb ?? raw.aTB ?? raw.ATB;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true';
    return false;
  }

  onAtbFilterChange(value: boolean, filter: (value: boolean) => void): void {
    filter(value);
  }

  onAtbFilterClear(filter: (value: null) => void): void {
    filter(null);
  }

  openRequis(row: EpidemiologyRow | EpidemiologyTableRow): void {
    const q = this.requestDetailQueryForRow(row);
    if (!q) return;

    const tree = this.router.createUrlTree(['/patient-requests', 'view-request'], {
      queryParams: q,
      queryParamsHandling: '',
    });
    const url = this.router.serializeUrl(tree);
    const origin = typeof globalThis !== 'undefined' && globalThis.location?.origin ? globalThis.location.origin : '';
    const full = origin + (url.startsWith('/') ? url : '/' + url);
    globalThis.open?.(full, '_blank', 'noopener,noreferrer');
  }

  private requestDetailQueryForRow(row: EpidemiologyRow | EpidemiologyTableRow): Record<string, string | number> | null {
    const requisNr = String(row.requisNr ?? '').trim();
    const entryDate = this.entryDateForRequestLink(row.entryDate);
    if (!requisNr || !entryDate) return null;
    const q: Record<string, string | number> = {
      laboratorio: this.detailLaboratorio,
      peticion: requisNr,
      labor: this.labNr,
      date: entryDate,
      impreso: '.',
      sitio: String(row.site ?? 'D').trim() || 'D',
      fromAcumulados: 1,
      fromEpidemiology: 1,
    };
    const centroId = this.currentCenterId();
    if (centroId != null) q['centroId'] = centroId;
    return q;
  }

  private entryDateForRequestLink(v: string | Date | null | undefined): string {
    if (v instanceof Date && !Number.isNaN(v.getTime())) {
      return v.toISOString();
    }
    return String(v ?? '').trim();
  }

  openSensitivityAtbView(): void {
    if (!this.canOpenSensitivityAtb) return;

    const q: Record<string, string | number> = {
      patientId: this.patientId,
      labNr: this.labNr,
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
    };
    if (this.detailLaboratorio) q['detailLaboratorio'] = this.detailLaboratorio;
    const c = this.currentCenterId();
    if (c != null && !Number.isNaN(Number(c))) q['centroId'] = Number(c);

    const tree = this.router.createUrlTree(['/patient-requests', 'sensitivity-atb-view'], {
      queryParams: q,
      queryParamsHandling: '',
    });
    const url = this.router.serializeUrl(tree);
    const origin = typeof globalThis !== 'undefined' && globalThis.location?.origin ? globalThis.location.origin : '';
    const full = origin + (url.startsWith('/') ? url : '/' + url);
    globalThis.open?.(full, '_blank', 'noopener,noreferrer');
  }

}
